import { Flex, Center, Span } from '@chakra-ui/react';
import { memo } from 'react';
import { subtract, size, gt } from 'lodash';
import { CloseIcon, UserItem, GroupItemRender } from '@halo/ui-chat-react';
import { useTranslation } from 'react-i18next';
import { FullBox } from '@halo/ui-react';

export interface SelectedListProps {
  checkUserKey?: boolean;
  height?: string;
  selected: any[];
  onRemove: (item: any) => void;
  extraElement?: React.ReactNode;
}

const SelectedList = ({ selected, onRemove, checkUserKey, extraElement }: SelectedListProps) => {
  const { t } = useTranslation();
  const GroupsSelected = selected.filter((item) => item?.isGroup);
  const groupSize = size(GroupsSelected);
  const userSize = subtract(size(selected), groupSize);

  const headerElement = useMemo(() => {
    const userElement = (
      <Span>
        {userSize} {userSize === 1 ? t('filter.membersOne') : t('filter.membersOther')}
      </Span>
    );
    const groupElement = (
      <Span>
        {groupSize} {groupSize === 1 ? t('filter.groupsOne') : t('filter.groupsOther')}
      </Span>
    );
    if (gt(userSize, 0) && gt(groupSize, 0)) {
      return (
        <>
          {userElement} {t('filter.and')} {groupElement}
        </>
      );
    } else if (gt(groupSize, 0)) {
      return groupElement;
    }
    return userElement;
  }, [groupSize, userSize, t]);

  return (
    <FullBox.Root minW="0" flex="1" p="3" h="full" column>
      <FullBox.Aside fontSize="xs" fontWeight="medium">
        <Span>{t('filter.selected')}</Span>
        {headerElement}
      </FullBox.Aside>
      <FullBox.Main className="scrollHover">
        {selected.map((item) => {
          const isKeyErr = checkUserKey ? !(item.have_group_key && item.have_user_key) : false;
          return (
            <Flex key={item.user_id} alignItems="center" gap="2">
              {item?.isGroup ? (
                <GroupItemRender
                  {...item}
                  avatar={item.avatar}
                  endElement={
                    <Center cursor="pointer" color="fg.subtle" fontSize="sm">
                      <CloseIcon onClick={() => onRemove(item)} />
                    </Center>
                  }
                />
              ) : (
                <UserItem
                  {...item}
                  error={isKeyErr}
                  avatar={item.avatar}
                  userExtraElement={
                    <Center cursor="pointer" color="fg.subtle">
                      <CloseIcon onClick={() => onRemove(item)} />
                    </Center>
                  }
                />
              )}
            </Flex>
          );
        })}
      </FullBox.Main>
      <FullBox.Aside>{extraElement}</FullBox.Aside>
    </FullBox.Root>
  );
};

const MemoizedSelectedList = memo(SelectedList);

export { MemoizedSelectedList as SelectedList };
